import {
  NavigationGuard,
  RouteLocationNormalized,
} from "vue-router";
import { useAuthStore } from "@/stores/authStore";

/**
 * Validates that dynamic route params (lakeId/orgSlug) follow our safe formats before
 * letting the guard continue. Prevents path-traversal style payloads or malformed IDs.
 */
export const validateRouteParams = (to: RouteLocationNormalized): boolean => {
  if (to.params.lakeId && typeof to.params.lakeId === "string") {
    const isValidId =
      /^[0-9a-fA-F]{24}$|^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/.test(
        to.params.lakeId,
      );
    if (!isValidId) return false;
  }

  if (to.params.orgSlug && typeof to.params.orgSlug === "string") {
    const isValidSlug = /^[a-zA-Z0-9\-_]+$/.test(to.params.orgSlug);
    if (!isValidSlug) return false;
  }

  return true;
};

/**
 * Guard pour les sections administrateur : exige un utilisateur authentifié
 * (ou un token valide en cache) et autorise uniquement les SuperAdmin. En
 * cas d'échec, on redirige vers la page `forbidden`.
 */
export const adminGuard: NavigationGuard = async (to, from, next) => {
  const authStore = useAuthStore();

  try {
    // `&& authStore.token` conditionnait cet appel à une ref jamais
    // assignée (authentification par cookie) : la récupération de session
    // était donc inatteignable. Le fail-closed juste en dessous reste seul
    // juge de l'accès.
    if (!authStore.user) {
      await authStore.initializeUser();
    }

    // Fail-CLOSED : un utilisateur non chargé/non authentifié ne doit jamais
    // franchir la zone admin. L'ancienne condition `!authStore.user || isSuperAdmin`
    // ouvrait l'accès quand `user` était null (aucun token, ou chargement échoué).
    if (!authStore.user) {
      return next({ name: "login" });
    }
    if (authStore.isSuperAdmin) {
      return next();
    }

    console.warn("⚠️ Tentative d'accès non autorisé à la zone admin:", {
      userId: authStore.user?.id,
      role: authStore.user?.role,
      route: to.path,
    });
    return next({ name: "forbidden" });
  } catch (error) {
    console.error("❌ Erreur dans adminGuard:", error);
    return next({ name: "login" });
  }
};

/**
 * Guard métier « administrateur d'organisation » : réserve un écran au rôle
 * ORGANIZATION_ADMIN (le super_admin passe aussi, au titre de la supervision
 * plateforme). Un simple MANAGER est refusé (vers `/forbidden`). À utiliser pour
 * les écrans sensibles au niveau organisation (facturation, équipe, utilisateurs)
 * que le `managerGuard` (manager OU admin) laissait fuiter au manager délégué.
 */
export const orgAdminGuard: NavigationGuard = async (to, from, next) => {
  const authStore = useAuthStore();

  try {
    if (!authStore.user) {
      await authStore.initializeUser();
    }

    if (!authStore.isAuthenticated) {
      return next({ name: "login" });
    }

    if (!authStore.isOrgAdmin && !authStore.isSuperAdmin) {
      console.warn("⚠️ Accès org-admin refusé:", {
        userId: authStore.user?.id,
        role: authStore.user?.role,
        route: to.path,
      });
      return next({ name: "forbidden" });
    }

    next();
  } catch (error) {
    console.error("❌ Erreur dans orgAdminGuard:", error);
    return next({ name: "login" });
  }
};

/**
 * Guard métier pour les managers : impose l’authentification, puis vérifie que
 * le rôle est MANAGER ou ORGANIZATION_ADMIN. Redirige vers `/login` si non
 * authentifié, vers `/forbidden` si le rôle n’est pas dans la liste blanche.
 */
export const managerGuard: NavigationGuard = async (to, from, next) => {
  const authStore = useAuthStore();

  try {
    // Voir adminGuard : condition sur une ref jamais assignée, donc appel
    // mort. La vérification d'authentification ci-dessous est inchangée.
    if (!authStore.user) {
      await authStore.initializeUser();
    }

    if (!authStore.isAuthenticated) {
      return next({ name: "login" });
    }

    // Source unique de vérité pour le rôle manager (mêmes règles que le store et
    // le sidebar), au lieu d'une liste blanche locale mêlant enum et littéral.
    if (!authStore.isManager) {
      return next({ name: "forbidden" });
    }

    next();
  } catch (error) {
    console.error("❌ Erreur dans managerGuard:", error);
    return next({ name: "login" });
  }
};
